import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { LayoutSite } from '../components/layout';
import { Header } from '../components/header';
import {
    ssh_list_years, ssh_list_files, ssh_start, ssh_status, ssh_stop, ssh_outputs, ssh_download_zip, ssh_concurrency, ssh_current
} from '../services/api.service';
import { SshFile, SshRunOptions } from '../types';
import { SIC_TO_NAME, KNOWN_SIC } from '../data/hippodromes';
import './ssh.scss';

const OPT_KEY = 'ssh.options';
const POLL_MS = 1500;
const MAX_LOG_LINES = 5000;
const SEP = '\x1f';                 // channel delimiter used by the backend log

type Channels = Record<string, string[]>;

/* -------------------------------------------------------------------------------- *\
|                               appendCapped
\* -------------------------------------------------------------------------------- */
/** Append lines, keeping at most MAX_LOG_LINES (drop the oldest). */
function appendCapped(prev: string[], add: string[]): string[]
{
    const next = prev.concat(add);
    return next.length > MAX_LOG_LINES ? next.slice(next.length - MAX_LOG_LINES) : next;
}
/* -------------------------------------------------------------------------------- *\
|                               parseLine
\* -------------------------------------------------------------------------------- */
/** Split a raw log line into its channel and text (general = no prefix). */
function parseLine(raw: string): { channel: string, text: string }
{
    if (raw.startsWith(SEP))
    {
        const end = raw.indexOf(SEP, 1);
        if (end > 0) return { channel: raw.slice(1, end), text: raw.slice(end + 1) };
    }
    return { channel: 'general', text: raw };
}
/** Order tabs: general first, then process-1, process-2, … numerically. */
function orderChannels(names: string[]): string[]
{
    const rest = names.filter(n => n !== 'general').sort((a, b) =>
    {
        const na = parseInt(a.replace(/\D/g, ''), 10), nb = parseInt(b.replace(/\D/g, ''), 10);
        return (Number.isFinite(na) && Number.isFinite(nb)) ? na - nb : a.localeCompare(b);
    });
    return ['general', ...rest];
}
/** Route raw lines to their channel and pull out the done/failed markers. */
function routeLines(rawLines: string[]): { perChannel: Channels, done: string[], failed: string[] }
{
    const perChannel: Channels = {};
    const done: string[] = [];
    const failed: string[] = [];
    for (const raw of rawLines)
    {
        const { channel, text } = parseLine(raw);
        (perChannel[channel] || (perChannel[channel] = [])).push(text);
        const ok = text.match(/\[result\] stored (.+?)\.json\b/i);
        if (ok) { done.push(ok[1]); continue; }
        const ko = text.match(/\[failed\] (\S+)/i);
        if (ko) failed.push(ko[1]);
    }
    return { perChannel, done, failed };
}
function mergeChannels(prev: Channels, perChannel: Channels): Channels
{
    const next = { ...prev };
    for (const ch of Object.keys(perChannel)) next[ch] = appendCapped(next[ch] || [], perChannel[ch]);
    return next;
}

type Status = 'present' | 'excluded' | 'done' | 'failed';

/* -------------------------------------------------------------------------------- *\
|                               helpers
\* -------------------------------------------------------------------------------- */
function levelOf(line: string): 'error' | 'warn' | 'info' | 'ssh'
{
    if (/\berror\b/i.test(line) || /\[erreur\]/i.test(line) || /failed/i.test(line)) return 'error';
    if (/\bwarn(ing)?\b/i.test(line) || line.startsWith('[!]')) return 'warn';
    if (line.startsWith('[SSH]') || line.startsWith('[proc]')) return 'ssh';
    return 'info';
}
function humanSize(n: number): string
{
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / 1024 / 1024).toFixed(1)} MB`;
}
/** Humanize a duration in seconds, e.g. "45 s", "3 min 07 s", "1 h 12 min". */
function humanDuration(sec: number): string
{
    const s = Math.max(0, Math.round(sec));
    if (s < 60) return `${s} s`;
    const m = Math.floor(s / 60), rs = s % 60;
    if (m < 60) return `${m} min ${String(rs).padStart(2, '0')} s`;
    const h = Math.floor(m / 60), rm = m % 60;
    return `${h} h ${String(rm).padStart(2, '0')} min`;
}
function loadJson<T>(key: string, def: T): T
{
    try { const raw = localStorage.getItem(key); if (raw) return JSON.parse(raw); } catch { /* ignore */ }
    return def;
}
function baseOf(name: string): string { return name.replace(/\.tar\.gz$/i, ''); }
const STATUS_LABEL: Record<Status, string> = { present: '✔ Présent', excluded: '⊘ Exclu (SIC)', done: '✔ Déjà fait', failed: '✗ Échoué' };
/* -------------------------------------------------------------------------------- *\
|                               audit CSV parsing
\* -------------------------------------------------------------------------------- */
/** One race extracted from the audit CSV, keyed by its archive base name. */
interface CsvRace { key: string; label: string }

/** Trim BOM / whitespace and strip surrounding double quotes from a CSV cell. */
function csvCell(c: string): string
{
    return c.replace(/^﻿/, '').trim().replace(/^"(.*)"$/s, '$1').trim();
}

/** "29/08/2025" (DD/MM/YYYY) → "20250829". Returns '' when it can't be parsed. */
function csvDate(raw: string): string
{
    const m = raw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (m) return `${m[3]}${m[2].padStart(2, '0')}${m[1].padStart(2, '0')}`;
    const digits = raw.replace(/\D/g, '');
    return digits.length === 8 ? digits : '';        // fallback: already YYYYMMDD
}

/**
 * Parse an audit CSV (";"-separated) into the unique set of races it references.
 * Column positions are read from the header row (Date / (Code) SIC / Course), so
 * every known layout is supported — including the one where a "Hippodrome" column
 * shifts "Course" one place to the right. Falls back to Date;Sic;Course when no
 * header is present. Each race becomes the archive base name the SSH scan uses:
 * YYYYMMDD + SIC code + course number (e.g. 20250829S-M7). SIC codes not in the
 * known list are still matched but reported so the user can be warned of a typo.
 */
function parseAuditCsv(text: string): { races: CsvRace[]; unknownSics: string[] }
{
    const lines = text.split(/\r?\n/).filter(l => l.trim() !== '');
    if (lines.length === 0) return { races: [], unknownSics: [] };

    // Column indices: default to the header-less layout Date;Sic;Course.
    let iDate = 0, iSic = 1, iCourse = 2, dataStart = 0;
    const head = lines[0].split(';').map(csvCell);
    if (!csvDate(head[0] || ''))    // first cell isn't a date → it's a header row
    {
        dataStart = 1;
        head.forEach((h, i) =>
        {
            const n = h.toLowerCase();
            if (n === 'date') iDate = i;
            else if (n.includes('sic')) iSic = i;
            else if (n === 'course') iCourse = i;
        });
    }

    const seen = new Set<string>();
    const races: CsvRace[] = [];
    const unknown = new Set<string>();

    for (let li = dataStart; li < lines.length; li++)
    {
        const cells = lines[li].split(';').map(csvCell);
        const date = csvDate(cells[iDate] || '');               // "29/08/2025"/"2025-08-29" -> "20250829"
        if (!date) continue;                                    // skips junk rows
        const sic = (cells[iSic] || '').toUpperCase();
        const num = ((cells[iCourse] || '').match(/\d+/) || [])[0] || '';   // "C7" -> "7"
        if (!sic || !num) continue;
        if (!KNOWN_SIC.has(sic)) unknown.add(sic);

        const key = `${date}${sic}${num}`;
        if (seen.has(key)) continue;
        seen.add(key);
        const name = SIC_TO_NAME.get(sic) || sic;
        races.push({ key, label: `${date} ${name} C${num}` });
    }
    return { races, unknownSics: Array.from(unknown) };
}

/** Outcome of applying an audit CSV to the current scan. */
interface CsvResult
{
    total: number;          // unique races in the CSV
    selected: number;       // races selectable & runnable in the current scan
    outOfDate: number;      // matched but hidden by the start/end date filter
    excluded: number;       // matched but SIC-excluded (locked, not selectable)
    notFound: CsvRace[];    // races absent from this scan (likely another year)
    unknownSics: string[];
}
/* -------------------------------------------------------------------------------- *\
|                               SshPage
\* -------------------------------------------------------------------------------- */
export function SshPage()
{
    const [opts, setOpts] = useState<SshRunOptions>(loadJson<SshRunOptions>(OPT_KEY, { relaunchMec: false }));

    const [years, setYears] = useState<string[]>([]);
    const [year, setYear] = useState<string>('');
    const [files, setFiles] = useState<SshFile[]>([]);
    const [selected, setSelected] = useState<Record<string, boolean>>({});
    const [busy, setBusy] = useState<string>('');
    const [error, setError] = useState<string>('');

    // "done" is authoritative from the server (RESULTS_DIR, via f.done). This set only
    // tracks files finished during the current session for live feedback (transient).
    const [processed, setProcessed] = useState<Set<string>>(new Set());
    // archives that failed in the current/last run (no result produced). Transient.
    const [failed, setFailed] = useState<Set<string>>(new Set());

    // result of the last audit-CSV selection (null when none was applied)
    const [csvResult, setCsvResult] = useState<CsvResult | null>(null);
    // when set, the file list is restricted to the archives referenced by the CSV
    const [csvKeys, setCsvKeys] = useState<Set<string> | null>(null);
    const csvInputRef = useRef<HTMLInputElement>(null);

    // scan filters
    const [showPresent, setShowPresent]   = useState<boolean>(true);
    const [showExcluded, setShowExcluded] = useState<boolean>(true);
    const [showDone, setShowDone]         = useState<boolean>(true);
    const [showFailed, setShowFailed]     = useState<boolean>(true);

    const [jobId, setJobId] = useState<string>('');
    const [running, setRunning] = useState<boolean>(false);
    const [exitCode, setExitCode] = useState<number | null>(null);
    const [channels, setChannels] = useState<Channels>({ general: [] });   // per-process log tabs
    const [activeTab, setActiveTab] = useState<string>('general');
    const [hasOutputs, setHasOutputs] = useState<boolean>(false);
    const [autoScroll, setAutoScroll] = useState<boolean>(true);
    const [optsOpen, setOptsOpen] = useState<boolean>(true);   // collapse the Options panel to enlarge the list

    // Progress of the current run, used to estimate the remaining time.
    const [runInfo, setRunInfo] = useState<{ startedAt: number; total: number } | null>(null);
    const [runDone, setRunDone] = useState<number>(0);       // archives finished (ok or failed)
    const [now, setNow] = useState<number>(Date.now());      // ticks while running for a live ETA

    const fromRef = useRef<number>(0);
    const contentRef = useRef<HTMLDivElement>(null);

    useEffect(() => { localStorage.setItem(OPT_KEY, JSON.stringify(opts)); }, [opts]);

    // On mount, rejoin the single global SSH job (if any). Because it lives on the
    // server — not in this client's storage — any client/reconnect recovers the same
    // run: its selection, logs, progress and ETA.
    useEffect(() =>
    {
        let cancelled = false;
        (async () =>
        {
            try
            {
                const cur = await ssh_current();
                if (cancelled || !cur) return;
                setJobId(cur.jobId);

                // Restore the selection as a synthetic file list (basename + remote path),
                // so the archives being processed are visible even without a fresh scan.
                const syn: SshFile[] = cur.paths.map(p =>
                {
                    const name = p.split('/').pop() || p;
                    return { name, size: 0, year: name.slice(0, 4), path: p, excluded: false, done: false };
                });
                setFiles(syn);
                const sel: Record<string, boolean> = {};
                syn.forEach(f => { sel[f.path] = true; });
                setSelected(sel);

                const st = await ssh_status(cur.jobId, 0);
                if (cancelled) return;
                const { perChannel, done, failed } = routeLines(st.lines);
                setChannels(mergeChannels({ general: [] }, perChannel));
                if (done.length) addProcessed(done);
                if (failed.length) addFailed(failed);
                fromRef.current = st.nextFrom;
                setExitCode(st.exitCode);
                setRunning(st.running);

                // Restore the ETA from the server: authoritative start time + total.
                if (cur.running && cur.total > 0)
                {
                    setRunInfo({ startedAt: cur.startedAt, total: cur.total });
                    setRunDone(done.length + failed.length);
                    setNow(Date.now());
                }
                if (!cur.running)
                {
                    try { setHasOutputs((await ssh_outputs(cur.jobId)).length > 0); } catch { /* ignore */ }
                }
            }
            catch { /* no reachable job — ignore */ }
        })();
        return () => { cancelled = true; };
    // eslint-disable-next-line
    }, []);

    const setO = (patch: Partial<SshRunOptions>) => setOpts(prev => ({ ...prev, ...patch }));

    const addProcessed = (names: string[]) =>
    {
        setProcessed(prev => { const next = new Set(prev); names.forEach(n => next.add(n)); return next; });
        // A success clears any previous "failed" mark (e.g. after a retry).
        setFailed(prev =>
        {
            if (!names.some(n => prev.has(n))) return prev;
            const next = new Set(prev);
            names.forEach(n => next.delete(n));
            return next;
        });
    };
    const addFailed = (names: string[]) =>
        setFailed(prev => { const next = new Set(prev); names.forEach(n => next.add(n)); return next; });

    /* -------------------------------------------------------------------------------- *\
    |                               status computation
    \* -------------------------------------------------------------------------------- */
    // Exclusion + "done" are authoritative from the server (EXCLUDE_SIC / RESULTS_DIR);
    // `processed`/`failed` add live feedback for the current session.
    const statusOf = useCallback((f: SshFile): Status =>
    {
        const b = baseOf(f.name);
        if (f.excluded) return 'excluded';
        if (failed.has(b)) return 'failed';
        if (f.done || processed.has(b)) return 'done';
        return 'present';
    }, [processed, failed]);

    /* -------------------------------------------------------------------------------- *\
    |                               listing
    \* -------------------------------------------------------------------------------- */
    const onListYears = async () =>
    {
        setError(''); setBusy('years'); setYears([]); setFiles([]); setSelected({});
        try
        {
            const { years: ys, access } = await ssh_list_years();
            if (access.length) setChannels(prev => mergeChannels(prev, { general: access }));
            setYears(ys);
            if (ys.length > 0) setYear(ys[0]);
        }
        catch (e: any) { setError(e?.response?.data?.message || e?.message || 'list error'); }
        finally { setBusy(''); }
    };

    const onListFiles = async () =>
    {
        if (!year) return;
        setError(''); setBusy('files'); setFiles([]); setSelected({}); setFailed(new Set()); setCsvResult(null); setCsvKeys(null);
        try
        {
            const { files: fs, access } = await ssh_list_files(year);
            if (access.length) setChannels(prev => mergeChannels(prev, { general: access }));
            setFiles(fs);
            // Pre-select only "present" files (exclus / déjà faits are deselected).
            const sel: Record<string, boolean> = {};
            fs.forEach(f => sel[f.path] = statusOf(f) === 'present');
            setSelected(sel);
        }
        catch (e: any) { setError(e?.response?.data?.message || e?.message || 'list error'); }
        finally { setBusy(''); }
    };

    const toggle = (f: SshFile) =>
    {
        if (statusOf(f) === 'excluded') return;   // excluded are locked
        setSelected(prev => ({ ...prev, [f.path]: !prev[f.path] }));
    };

    // Once a file is processed without error (its output was produced → it lands in
    // `processed`), uncheck it automatically.
    useEffect(() =>
    {
        setSelected(prev =>
        {
            let changed = false;
            const next = { ...prev };
            files.forEach(f =>
            {
                if (prev[f.path] && processed.has(baseOf(f.name))) { next[f.path] = false; changed = true; }
            });
            return changed ? next : prev;
        });
    }, [processed, files]);

    // Keep only files whose date (name prefix YYYYMMDD) is within the range.
    const inDates = useCallback((name: string): boolean =>
    {
        const d = name.slice(0, 8);
        if (opts.startDate && d < opts.startDate) return false;
        if (opts.endDate && d > opts.endDate) return false;
        return true;
    }, [opts.startDate, opts.endDate]);

    // After a CSV import, only the archives listed in it remain visible/selectable.
    const csvFiles = useMemo(
        () => csvKeys ? files.filter(f => csvKeys.has(baseOf(f.name))) : files,
        [files, csvKeys]);
    const dateFiles = useMemo(() => csvFiles.filter(f => inDates(f.name)), [csvFiles, inDates]);

    const visible = (st: Status) =>
        (st === 'present' && showPresent) || (st === 'excluded' && showExcluded)
        || (st === 'done' && showDone) || (st === 'failed' && showFailed);

    // Only affect the files currently displayed (respecting date + status filters);
    // leave hidden files' selection untouched. Excluded files are never selectable.
    const setAll = (v: boolean) =>
        setSelected(prev =>
        {
            const next = { ...prev };
            dateFiles.forEach(f =>
            {
                const st = statusOf(f);
                if (st !== 'excluded' && visible(st)) next[f.path] = v;
            });
            return next;
        });

    /* -------------------------------------------------------------------------------- *\
    |                               audit CSV selection
    \* -------------------------------------------------------------------------------- */
    // Restrict the list to the races of the audit CSV and select the matching ones.
    // Matching is by archive base name (YYYYMMDD + SIC + course).
    const applyCsvRaces = useCallback((races: CsvRace[], unknownSics: string[]) =>
    {
        const keys = new Set(races.map(r => r.key));
        const byKey = new Map<string, SshFile>();
        files.forEach(f => byKey.set(baseOf(f.name), f));

        const nextSel: Record<string, boolean> = {};
        files.forEach(f => { nextSel[f.path] = false; });   // start from all-deselected

        let selCount = 0, outOfDate = 0, excluded = 0;
        const notFound: CsvRace[] = [];
        races.forEach(r =>
        {
            const f = byKey.get(r.key);
            if (!f) { notFound.push(r); return; }
            if (f.excluded) { excluded++; return; }          // locked, cannot be selected
            nextSel[f.path] = true;
            if (inDates(f.name)) selCount++; else outOfDate++;
        });

        setCsvKeys(keys);                                    // only these archives stay visible
        setSelected(nextSel);
        // Reveal every status so the freshly selected files are actually visible.
        setShowPresent(true); setShowExcluded(true); setShowDone(true); setShowFailed(true);
        setCsvResult({ total: races.length, selected: selCount, outOfDate, excluded, notFound, unknownSics });
    }, [files, inDates]);

    // Drop the CSV filter and go back to the full scan listing.
    const clearCsv = () => { setCsvKeys(null); setCsvResult(null); };

    const onCsvFile = async (e: React.ChangeEvent<HTMLInputElement>) =>
    {
        const file = e.target.files?.[0];
        e.target.value = '';                                 // allow re-selecting the same file
        if (!file) return;
        if (files.length === 0) { setError("Scannez d'abord une année avant de charger un CSV."); return; }
        setError('');
        try
        {
            const { races, unknownSics } = parseAuditCsv(await file.text());
            if (races.length === 0)
            {
                setError("Aucune course reconnue dans le CSV.");
                return;
            }
            applyCsvRaces(races, unknownSics);
        }
        catch (err: any) { setError(err?.message || 'CSV parse error'); }
    };

    // Selection is limited to in-date, non-excluded files.
    const selectedPaths = dateFiles.filter(f => selected[f.path] && statusOf(f) !== 'excluded').map(f => f.path);

    const counts = useMemo(() =>
    {
        const c = { present: 0, excluded: 0, done: 0, failed: 0 };
        dateFiles.forEach(f => { c[statusOf(f)]++; });
        return c;
    }, [dateFiles, statusOf]);

    /* -------------------------------------------------------------------------------- *\
    |                               poll
    \* -------------------------------------------------------------------------------- */
    const poll = useCallback(async (id: string) =>
    {
        try
        {
            const st = await ssh_status(id, fromRef.current);
            if (st.lines.length > 0)
            {
                fromRef.current = st.nextFrom;
                const { perChannel, done, failed } = routeLines(st.lines);
                setChannels(prev => mergeChannels(prev, perChannel));
                if (done.length > 0) addProcessed(done);
                if (failed.length > 0) addFailed(failed);
                // Count every completion (result or failure) for the ETA estimate.
                if (done.length + failed.length > 0) setRunDone(d => d + done.length + failed.length);
            }
            setRunning(st.running);
            setExitCode(st.exitCode);
            if (!st.running)
            {
                try
                {
                    const outs = await ssh_outputs(id);
                    setHasOutputs(outs.length > 0);
                    if (outs.length > 0) addProcessed(outs.map(o => o.name.replace(/\.json$/i, '')));
                }
                catch { /* ignore */ }
            }
        }
        catch (e: any) { setError(e?.message || 'status error'); }
    // eslint-disable-next-line
    }, []);

    useEffect(() =>
    {
        if (!jobId || !running) return;
        const interval = setInterval(() => poll(jobId), POLL_MS);
        return () => clearInterval(interval);
    }, [jobId, running, poll]);

    // Tick every second while running so the elapsed time / ETA stay live.
    useEffect(() =>
    {
        if (!running) return;
        const t = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(t);
    }, [running]);

    // Progress + remaining-time estimate for the current run.
    const progress = useMemo(() =>
    {
        if (!runInfo) return null;
        const elapsed = Math.max(0, (now - runInfo.startedAt) / 1000);
        const eta = runDone > 0 ? (elapsed / runDone) * (runInfo.total - runDone) : null;
        return { elapsed, done: runDone, total: runInfo.total, eta };
    }, [runInfo, runDone, now]);

    const activeLines = useMemo(() => channels[activeTab] || [], [channels, activeTab]);
    useEffect(() =>
    {
        const el = contentRef.current;
        if (el && autoScroll) el.scrollTop = el.scrollHeight;
    }, [activeLines, autoScroll]);

    /* -------------------------------------------------------------------------------- *\
    |                               concurrency change (live)
    \* -------------------------------------------------------------------------------- */
    const onConcurrencyChange = (n: number) =>
    {
        const val = Math.min(50, Math.max(1, n || 1));
        setO({ concurrency: val });
        if (running && jobId) { ssh_concurrency(jobId, val).catch(() => { /* ignore */ }); }
    };

    /* -------------------------------------------------------------------------------- *\
    |                               run / stop
    \* -------------------------------------------------------------------------------- */
    const onRun = async () =>
    {
        setError('');
        if (selectedPaths.length === 0) { setError("Sélectionnez au moins un fichier."); return; }
        setChannels({ general: [] }); setActiveTab('general'); setHasOutputs(false); fromRef.current = 0; setExitCode(null); setFailed(new Set());
        setRunDone(0); setRunInfo({ startedAt: Date.now(), total: selectedPaths.length }); setNow(Date.now());
        try
        {
            const { jobId: id } = await ssh_start(selectedPaths, opts);
            setJobId(id); setRunning(true); poll(id);
        }
        catch (e: any) { setError(e?.response?.data?.message || e?.message || 'start error'); }
    };
    const onStop = async () => { if (jobId) { try { await ssh_stop(jobId); } catch (e: any) { setError(e?.message); } } };

    return (
        <LayoutSite>
            <div className="ssh">
                <Header title="SSH Relaunch" />

                <div className="ssh__body">
                    <div className="ssh__left">
                        {/* --- remote listing (connection configured server-side) --- */}
                        <div className="ssh__panel">
                            <div className="ssh__panel__title">Serveur distant</div>
                            <div className="ssh__hint">Connexion configurée côté serveur (.env)</div>
                            <div className="ssh__listbar">
                                <button onClick={onListYears} disabled={busy !== ''}>
                                    {busy === 'years' ? '…' : 'Lister les années'}
                                </button>
                                {years.length > 0 && (
                                    <>
                                        <select value={year} onChange={e => setYear(e.target.value)}>
                                            {years.map(y => <option key={y} value={y}>{y}</option>)}
                                        </select>
                                        <button onClick={onListFiles} disabled={busy !== ''}>
                                            {busy === 'files' ? '…' : 'Scanner'}
                                        </button>
                                    </>
                                )}
                            </div>
                        </div>

                        {/* --- options --- */}
                        <div className="ssh__panel">
                            <button className="ssh__panel__title ssh__panel__toggle" onClick={() => setOptsOpen(o => !o)}>
                                <span>{optsOpen ? '▾' : '▸'}</span> Options
                            </button>
                            {optsOpen && (
                            <>
                            <label className="ssh__check ssh__check__hi">
                                <input type="checkbox" checked={opts.relaunchMec} onChange={e => setO({ relaunchMec: e.target.checked })} />
                                Relancer la MEC
                            </label>
                            {opts.relaunchMec && (
                                <label className="ssh__check" title="Renomme parcours.csv en parcours_old.csv, ajoute les furlongs au parcours puis recalcule l'interpolation avant la MEC">
                                    <input type="checkbox" checked={!!opts.addYards} onChange={e => setO({ addYards: e.target.checked })} />
                                    Add yards
                                </label>
                            )}
                            <label className="ssh__field"><span>Process simultanés</span>
                                <input type="number" min={1} max={50} value={opts.concurrency ?? 1}
                                       onChange={e => onConcurrencyChange(parseInt(e.target.value, 10))} /></label>
                            <label className="ssh__field"><span>Deploy API key</span>
                                <input value={opts.deploy || ''} onChange={e => setO({ deploy: e.target.value })} placeholder="(optionnel)" /></label>
                            <div className="ssh__row2">
                                <label className="ssh__field"><span>Start date</span>
                                    <input value={opts.startDate || ''} onChange={e => setO({ startDate: e.target.value })} placeholder="YYYYMMDD" /></label>
                                <label className="ssh__field"><span>End date</span>
                                    <input value={opts.endDate || ''} onChange={e => setO({ endDate: e.target.value })} placeholder="YYYYMMDD" /></label>
                            </div>
                            <label className="ssh__field"><span>Turfinfo cache dir</span>
                                <input value={opts.cacheDir || ''} onChange={e => setO({ cacheDir: e.target.value })} placeholder="(optionnel)" /></label>
                            <div className="ssh__checks">
                                <label className="ssh__check"><input type="checkbox" checked={!!opts.useCache} onChange={e => setO({ useCache: e.target.checked })} />cache turfinfo</label>
                                {!opts.relaunchMec && (
                                    <label className="ssh__check"><input type="checkbox" checked={!!opts.verbose} onChange={e => setO({ verbose: e.target.checked })} />verbose</label>
                                )}
                            </div>
                            </>
                            )}
                        </div>

                        {/* --- files (scan result) --- */}
                        {files.length > 0 && (
                            <div className="ssh__panel ssh__panel__files">
                                <div className="ssh__scanline">
                                    {dateFiles.length} fichier(s){dateFiles.length !== files.length ? ` (sur ${files.length}${csvKeys ? ', filtre CSV' : ', filtre date'})` : ''} — {counts.present} présent(s), {counts.excluded} exclu(s), {counts.done} déjà fait(s), {counts.failed} échoué(s)
                                </div>
                                <div className="ssh__filters">
                                    <label><input type="checkbox" checked={showPresent} onChange={e => setShowPresent(e.target.checked)} />Présents</label>
                                    <label><input type="checkbox" checked={showExcluded} onChange={e => setShowExcluded(e.target.checked)} />Exclus</label>
                                    <label><input type="checkbox" checked={showDone} onChange={e => setShowDone(e.target.checked)} />Déjà faits</label>
                                    <label><input type="checkbox" checked={showFailed} onChange={e => setShowFailed(e.target.checked)} />Échoués</label>
                                </div>
                                <div className="ssh__filesbar">
                                    <span>{selectedPaths.length}/{dateFiles.length} sélectionné(s)</span>
                                    <button onClick={() => setAll(true)}>Tout</button>
                                    <button onClick={() => setAll(false)}>Aucun</button>
                                    <button onClick={() => csvInputRef.current?.click()} title="Sélectionner les courses listées dans un fichier CSV d'audit">Depuis CSV…</button>
                                    <input ref={csvInputRef} type="file" accept=".csv,text/csv" hidden onChange={onCsvFile} />
                                </div>
                                {csvResult && (
                                    <div className="ssh__csv">
                                        <div className="ssh__csv__head">
                                            <span>CSV : {csvResult.selected}/{csvResult.total} course(s) sélectionnée(s)
                                                {csvResult.outOfDate > 0 && <> — <span className="ssh__csv__warn">{csvResult.outOfDate} hors filtre date</span></>}
                                                {csvResult.excluded > 0 && <> — <span className="ssh__csv__warn">{csvResult.excluded} exclu(s) SIC</span></>}
                                                {csvResult.notFound.length > 0 && <> — <span className="ssh__csv__warn">{csvResult.notFound.length} absente(s) de ce scan</span></>}
                                            </span>
                                            <button onClick={clearCsv} title="Afficher de nouveau tous les fichiers du scan">✕ filtre CSV</button>
                                        </div>
                                        {csvResult.unknownSics.length > 0 && (
                                            <div className="ssh__csv__warn">Code(s) SIC inconnu(s) : {csvResult.unknownSics.join(', ')}</div>
                                        )}
                                        {csvResult.notFound.length > 0 && (
                                            <div className="ssh__csv__list" title={csvResult.notFound.map(r => r.label).join('\n')}>
                                                Absentes : {csvResult.notFound.slice(0, 8).map(r => r.label).join(' · ')}{csvResult.notFound.length > 8 ? ' …' : ''}
                                            </div>
                                        )}
                                    </div>
                                )}
                                <div className="ssh__files">
                                    {dateFiles.filter(f => visible(statusOf(f))).map(f =>
                                    {
                                        const st = statusOf(f);
                                        return (
                                            <label key={f.path} className={`ssh__files__item ssh__files__item__${st}`}>
                                                <input type="checkbox" disabled={st === 'excluded'}
                                                       checked={!!selected[f.path] && st !== 'excluded'} onChange={() => toggle(f)} />
                                                <span className="ssh__files__name">{f.name}</span>
                                                <span className="ssh__files__status">{STATUS_LABEL[st]}</span>
                                                <span className="ssh__files__size">{f.size > 0 ? humanSize(f.size) : ''}</span>
                                            </label>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        <div className="ssh__actions">
                            <button className="ssh__run" onClick={onRun} disabled={running}>▶ Run</button>
                            <button className="ssh__stop" onClick={onStop} disabled={!running}>■ Stop</button>
                            <span className={'ssh__state ' + (running ? 'ssh__state__run' : '')}>
                                {running ? 'en cours…' : exitCode === null ? '' : `terminé (code ${exitCode})`}
                            </span>
                        </div>
                        {running && progress && (
                            <div className="ssh__eta">
                                {progress.done}/{progress.total} fait(s) en {humanDuration(progress.elapsed)}
                                {progress.eta != null
                                    ? <> — reste ~ <strong>{humanDuration(progress.eta)}</strong></>
                                    : <> — estimation dès la 1<sup>re</sup> course terminée…</>}
                            </div>
                        )}
                        {hasOutputs && !running && (
                            <button className="ssh__zip" onClick={() => ssh_download_zip(jobId)}>⬇ Télécharger les résultats (.zip)</button>
                        )}
                        {error && <div className="ssh__error">{error}</div>}
                    </div>

                    {/* --- log (tabs: general + one per process) --- */}
                    <div className="ssh__logwrap">
                        <div className="ssh__tabs">
                            {orderChannels(Object.keys(channels)).map(ch => (
                                <button key={ch}
                                        className={'ssh__tab' + (ch === activeTab ? ' ssh__tab__active' : '')}
                                        onClick={() => setActiveTab(ch)}>
                                    {ch === 'general' ? 'Général' : ch}
                                </button>
                            ))}
                        </div>
                        <div className="ssh__logbar">
                            <label><input type="checkbox" checked={autoScroll} onChange={e => setAutoScroll(e.target.checked)} />auto scroll</label>
                            <button onClick={() => setChannels(prev => ({ ...prev, [activeTab]: [] }))}>clear</button>
                            <span className="ssh__count">{activeLines.length} lignes</span>
                        </div>
                        <div className="ssh__log" ref={contentRef}>
                            {activeLines.map((line, i) => (
                                <div key={i} className={`ssh__line ssh__line__${levelOf(line)}`}>{line}</div>
                            ))}
                            {activeLines.length === 0 && <div className="ssh__line ssh__line__info">(pas de sortie)</div>}
                        </div>
                    </div>
                </div>
            </div>
        </LayoutSite>
    );
}
